// Vite 的 `?url` 由构建器解析，eslint 的 import/no-unresolved 不认识这个后缀。
// eslint-disable-next-line import/no-unresolved
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import type { AgentDocument } from '../agent/agentClient';

const TEXT_TYPES = new Set([
  'text/plain', 'text/markdown', 'text/csv', 'application/json', 'application/xml',
]);

/**
 * 文档不直接上传给模型：浏览器先抽出可见文本，服务端只收文本。
 * 这样 PDF 也能被 DeepSeek 当上下文使用，同时避免把二进制/本地路径交出去。
 */
export async function fileToChatDocument(file: File): Promise<AgentDocument> {
  if (file.size > 20 * 1024 * 1024) throw new Error('文档太大（最大 20MB）');
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
    // 动态加载 PDF.js，避免没发文档的人也下载整套解析器。
    const pdfjsLib = await import('pdfjs-dist');
    pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
    const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(await file.arrayBuffer()) }).promise;
    const pages: string[] = [];
    const count = Math.min(pdf.numPages, 120);
    for (let i = 1; i <= count; i += 1) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      pages.push(content.items
        .map((item) => ('str' in item ? item.str : ''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim());
    }
    const text = pages.filter(Boolean).join('\n\n').slice(0, 120_000);
    if (!text) throw new Error('PDF 里没有可提取的文字（可能是扫描件）');
    return { name: file.name.slice(0, 200), text, mimeType: 'application/pdf' };
  }
  if (TEXT_TYPES.has(file.type) || /\.(txt|md|markdown|csv|json|xml)$/i.test(file.name)) {
    const text = (await file.text()).slice(0, 120_000);
    if (!text.trim()) throw new Error('文档是空的');
    return { name: file.name.slice(0, 200), text, mimeType: file.type || 'text/plain' };
  }
  // 不限制格式：暂时读不了的二进制也先附上，让余白至少知道作者发了什么。
  return {
    name: file.name.slice(0, 200),
    text: `（这个格式暂时不能抽取文本，已作为附件发送：${file.name}，${Math.round(file.size / 1024)}KB）`,
    mimeType: file.type || 'application/octet-stream',
  };
}
